import { useState } from 'react'
import { Avatar } from './Avatar'
import { BottomSheet } from './BottomSheet'
import { Pressable } from './Pressable'
import { StatusChip } from './Chip'
import { useNls } from '../../nls/nlsProvider'
import './childSwitcher.css'

export interface ChildSwitcherItem {
  key: string
  name: string
  photoUrl?: string | null
  secondaryLine?: string
  frozen?: boolean
}

export interface ChildSwitcherProps {
  items: ChildSwitcherItem[]
  selectedKey: string
  onSelect: (key: string) => void
  ariaLabel: string
  sheetTitle: string
  className?: string
}

export const ChildSwitcher = ({
  items,
  selectedKey,
  onSelect,
  ariaLabel,
  sheetTitle,
  className,
}: ChildSwitcherProps) => {
  const { t } = useNls()
  const [isOpen, setIsOpen] = useState(false)

  if (items.length === 0) {
    return null
  }

  const selectedChild = items.find((c) => c.key === selectedKey) ?? items[0]

  if (items.length === 1) {
    return (
      <div className={['childSwitcher', 'childSwitcher--single', className].filter(Boolean).join(' ')}>
        <div className="childSwitcher__pill">
          <Avatar photoURL={selectedChild.photoUrl} name={selectedChild.name} size="sm" />
          <div className="childSwitcher__meta">
            <div className="childSwitcher__name">{selectedChild.name}</div>
            {selectedChild.secondaryLine && (
              <div className="childSwitcher__secondary">{selectedChild.secondaryLine}</div>
            )}
          </div>
          {selectedChild.frozen && (
            <StatusChip tone="warning" size="sm">
              {t('NLSFamilyChildPaused')}
            </StatusChip>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className={['childSwitcher', className].filter(Boolean).join(' ')}>
      <Pressable
        className="childSwitcher__pill childSwitcher__pill--interactive"
        onClick={() => setIsOpen(true)}
        aria-label={ariaLabel}
      >
        <Avatar photoURL={selectedChild.photoUrl} name={selectedChild.name} size="sm" />
        <div className="childSwitcher__meta">
          <div className="childSwitcher__name">
            {selectedChild.name} <span className="childSwitcher__chevron" aria-hidden="true">▾</span>
          </div>
          {selectedChild.secondaryLine && (
            <div className="childSwitcher__secondary">{selectedChild.secondaryLine}</div>
          )}
        </div>
        {selectedChild.frozen && (
          <StatusChip tone="warning" size="sm">
            {t('NLSFamilyChildPaused')}
          </StatusChip>
        )}
      </Pressable>

      <BottomSheet
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title={sheetTitle}
      >
        <ul className="childSwitcher__sheetList">
          {items.map((child) => {
            const isSelected = child.key === selectedKey
            return (
              <li key={child.key} className="childSwitcher__sheetItemWrapper">
                <Pressable
                  className={`childSwitcher__sheetItem ${isSelected ? 'is-selected' : ''}`}
                  onClick={() => {
                    onSelect(child.key)
                    setIsOpen(false)
                  }}
                  aria-current={isSelected ? 'true' : undefined}
                >
                  <Avatar photoURL={child.photoUrl} name={child.name} size="sm" />
                  <div className="childSwitcher__meta">
                    <div className="childSwitcher__name">{child.name}</div>
                    {child.secondaryLine && (
                      <div className="childSwitcher__secondary">{child.secondaryLine}</div>
                    )}
                  </div>
                  {child.frozen && (
                    <StatusChip tone="warning" size="sm">
                      {t('NLSFamilyChildPaused')}
                    </StatusChip>
                  )}
                </Pressable>
              </li>
            )
          })}
        </ul>
      </BottomSheet>
    </div>
  )
}
